import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, AlertTriangle } from "lucide-react";

/**
 * Final action bar at the bottom of the page: variation-count picker (3
 * fast / 5 standard) plus the big orange "Generate" button. Disabled
 * until a photo + style are picked; surfaces a small hint when blocked
 * so the user knows which step is missing.
 *
 * iter 81 (06.06.2026 — production feedback): added per-category scarcity
 * preview. When the user has picked a style + categories AND the smallest
 * selected category has fewer FDP product photos than the requested
 * variation count, we show an amber note explaining the practical limit
 * so the user can either lower the count or pick richer categories.
 */
export default function FinalActionBar({
  variationCount,
  onVariationCountChange,
  onSubmit,
  loading,
  file,
  style,
  catalog,
  selectedCats = [],
  usingCatalogPicker = false,
}) {
  const { t } = useTranslation();

  const options = [
    { v: 3, label: t("simulate.var_count_fast"), time: "~30-50s" },
    { v: 5, label: t("simulate.var_count_standard"), time: "~60-90s" },
  ];

  const tooltip = !file
    ? t("simulate.tooltip_no_file")
    : !style
    ? t("simulate.tooltip_no_style")
    : t("simulate.tooltip_ready");

  // Compute per-category scarcity for the currently-selected style. We
  // surface the WEAKEST link so the user knows the practical render
  // ceiling. Skip the calculation when the picker is inactive or the user
  // hasn't picked anything yet — there's nothing useful to warn about.
  let scarcity = null;
  if (
    usingCatalogPicker &&
    style &&
    selectedCats.length > 0 &&
    catalog?.categories
  ) {
    const rows = catalog.categories
      .filter((c) => selectedCats.includes(c.slug))
      .map((c) => ({
        name: c.name,
        count:
          (c.photos_by_style && c.photos_by_style[style]) ||
          c.uploaded_photos_count ||
          0,
      }))
      .sort((a, b) => a.count - b.count);
    // Only warn when at least one selected category has fewer photos than
    // requested variations — otherwise we'd nag users for nothing.
    const bottleneck = rows[0];
    if (bottleneck && bottleneck.count < variationCount) {
      scarcity = {
        bottleneck,
        maxDistinct: Math.max(1, bottleneck.count),
        breakdown: rows.slice(0, 5),
      };
    }
  }

  return (
    <section className="mt-12 max-w-2xl mx-auto" data-testid="final-action-bar">
      <div className="border border-orange-500/30 bg-gradient-to-b from-zinc-900/60 to-zinc-950/60 rounded-xl p-6 md:p-8 shadow-xl">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
          {t("simulate.var_count_title")}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {options.map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => onVariationCountChange(opt.v)}
              className={`p-3 rounded-md border-2 text-center transition ${
                variationCount === opt.v
                  ? "border-orange-500 bg-orange-500/10"
                  : "border-white/10 bg-zinc-950/40 hover:border-white/30"
              }`}
              data-testid={`var-count-${opt.v}`}
            >
              <div className="font-outfit font-black text-white text-2xl leading-none">
                {opt.v}
              </div>
              <div className="text-[10px] text-zinc-400 mt-1 uppercase tracking-wider">
                {opt.label}
              </div>
              <div className="text-[9px] text-zinc-500">{opt.time}</div>
            </button>
          ))}
        </div>

        <p className="text-xs text-zinc-500 mt-4 text-center">
          {t("simulate.var_count_hint", { count: variationCount })}
        </p>

        {scarcity && (
          <div
            className="mt-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-3.5"
            data-testid="scarcity-warning"
          >
            <div className="flex items-start gap-2.5">
              <AlertTriangle
                size={16}
                className="text-amber-400 mt-0.5 shrink-0"
                strokeWidth={2}
              />
              <div className="text-xs text-amber-200/95 leading-relaxed">
                <div className="font-bold text-amber-300 mb-1">
                  {t("simulate.scarcity_per_cat_title", {
                    name: scarcity.bottleneck.name,
                    count: scarcity.bottleneck.count,
                    style: style.replace("_", " "),
                  })}
                </div>
                <p>
                  {t("simulate.scarcity_per_cat_explain", {
                    requested: variationCount,
                    distinct: scarcity.maxDistinct,
                  })}
                </p>
                {scarcity.breakdown.length > 1 && (
                  <ul className="mt-2 space-y-0.5 text-[11px] opacity-90">
                    {scarcity.breakdown.map((r) => (
                      <li key={r.name}>
                        <span className="font-mono">•</span>{" "}
                        <strong>{r.name}</strong>: {r.count}{" "}
                        {r.count === 1
                          ? t("simulate.catalog_profile_singular")
                          : t("simulate.catalog_profile_plural")}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-2 text-[11px] opacity-80">
                  {t("simulate.scarcity_per_cat_advice")}
                </p>
              </div>
            </div>
          </div>
        )}

        <Button
          onClick={onSubmit}
          disabled={loading || !file || !style}
          className="w-full h-14 mt-4 bg-orange-500 hover:bg-orange-600 text-white btn-orange-glow text-base"
          data-testid="simulate-submit-btn"
          title={tooltip}
        >
          {loading ? (
            <>
              <Loader2 size={18} className="animate-spin mr-2" />{" "}
              {t("simulate.submit_loading")}
            </>
          ) : (
            <>
              <Sparkles size={18} strokeWidth={1.5} className="mr-2" />{" "}
              {t("simulate.submit_btn", { count: variationCount })}
            </>
          )}
        </Button>
        {!loading && (!file || !style) && (
          <p
            className="text-[11px] text-amber-400/90 mt-2 text-center"
            data-testid="simulate-submit-hint"
          >
            {!file ? t("simulate.hint_no_file") : t("simulate.hint_no_style")}
          </p>
        )}
      </div>
    </section>
  );
}
